import { Component } from '@theme/component';
import { ThemeEvents, SlideshowSelectEvent, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #controller = new AbortController();
  #programmaticPause = new WeakSet();
  #programmaticPlay = new WeakSet();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.hasAttribute('data-autoplay-visible-video')) {
      this.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
      this.#setupInlineSlideVideos(signal);

      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        requestAnimationFrame(() => {
          if (!this.isConnected) return;
          this.#syncInlineVideoPlayback();
        });
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  #handleSlideshowSelect = (event) => {
    this.#syncInlineVideoPlayback(event.detail.slide);
  };

  #setupInlineSlideVideos(signal) {
    this.#getInlineSlideVideos().forEach((video) => {
      if (video.dataset.inlineSlideVideoBound === 'true') return;

      video.dataset.inlineSlideVideoBound = 'true';
      video.dataset.autoplaySuppressed = 'false';
      video.dataset.pendingAutoplay = 'false';
      video.dataset.slideActive = 'false';

      video.addEventListener(
        'play',
        () => {
          const isProgrammaticPlay = this.#programmaticPlay.delete(video);

          if (!isProgrammaticPlay) {
            video.dataset.autoplaySuppressed = 'false';
          }
        },
        { signal }
      );

      video.addEventListener(
        'pause',
        () => {
          const isProgrammaticPause = this.#programmaticPause.delete(video);

          if (!isProgrammaticPause) {
            video.dataset.autoplaySuppressed = 'true';
          }
        },
        { signal }
      );

      video.addEventListener(
        'ended',
        () => {
          video.dataset.autoplaySuppressed = 'false';
          video.dataset.pendingAutoplay = 'false';
          video.currentTime = 0;
        },
        { signal }
      );

      video.addEventListener(
        'click',
        (event) => {
          if (event.target !== video) return;

          if (video.paused) {
            video.dataset.autoplaySuppressed = 'false';
            this.#playInlineVideo(video);
          } else {
            video.pause();
          }
        },
        { signal }
      );

      video.addEventListener(
        'dblclick',
        (event) => {
          event.preventDefault();
        },
        { signal }
      );

      video.addEventListener(
        'canplay',
        () => {
          if (video.dataset.pendingAutoplay != 'true') return;
          if (video.dataset.slideActive != 'true') return;
          if (video.dataset.autoplaySuppressed == 'true') return;

          video.dataset.pendingAutoplay = 'false';
          this.#playInlineVideo(video);
        },
        { signal }
      );
    });
  }

  #syncInlineVideoPlayback(activeSlideOverride) {
    if (!this.hasAttribute('data-autoplay-visible-video') || this.presentation !== 'carousel') return;

    const slideshow = this.slideshow;
    if (!slideshow) return;

    const videos = this.#getInlineSlideVideos();
    if (!videos.length) return;

    if (slideshow.visibleSlides.length !== 1) {
      videos.forEach((video) => this.#pauseInlineVideo(video));
      return;
    }

    const slides = slideshow.refs.slides || [];
    const activeSlide =
      activeSlideOverride ||
      slides.find((slide) => slide.getAttribute('aria-hidden') === 'false') ||
      slides[slideshow.current];

    videos.forEach((video) => {
      video.dataset.slideActive = activeSlide?.contains(video) ? 'true' : 'false';

      if (activeSlide?.contains(video)) {
        if (video.dataset.autoplaySuppressed != 'true') {
          this.#playInlineVideo(video);
        }
      } else {
        this.#pauseInlineVideo(video);
      }
    });
  }

  #playInlineVideo(video) {
    if (!video.paused) return;

    if (video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
      video.dataset.pendingAutoplay = 'true';

      if (video.networkState === HTMLMediaElement.NETWORK_EMPTY) {
        video.load();
      }
      return;
    }

    video.dataset.pendingAutoplay = 'false';
    this.#programmaticPlay.add(video);
    video.play().catch(() => {
      this.#programmaticPlay.delete(video);
    });
  }

  #pauseInlineVideo(video) {
    if (video.paused) return;

    video.dataset.pendingAutoplay = 'false';
    this.#programmaticPause.add(video);
    video.pause();
  }

  #getInlineSlideVideos() {
    return Array.from(this.querySelectorAll('video.product-media__inline-slide-video, video[data-inline-slide-video]'));
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
